/*
  Cart goals: one bar with milestones (free shipping + free gift) and the automatic free gift.
  Settings: Theme settings > Free shipping > Free gift.

  1. <cart-goals> updates the bar, milestones and message on every cart change.
     The gift line itself is not counted towards the goals.
  2. When the cart reaches the gift goal, a gift is added automatically: the Gift product, or
     one in-stock product picked at random from the Gift collection. It carries the hidden
     property "_free_gift" (used by the theme) and a visible one ("Free gift: Orders over $225")
     so it is clear in checkout and on the order. Quantity is kept at 1.
     It is removed again if the cart drops below the goal. If the customer removes the gift
     themselves, it is not re-added this session (until the cart drops below the goal again).
  3. Safety: the theme never prices the gift. If the gift is not free in the cart (the Buy X
     get Y discount is missing, expired or blocked by another code), it is taken out again and
     not re-added this session, so nobody is charged for it.
*/
(function () {
  'use strict';

  var isGiftLine = function (item) {
    return !!(item && item.properties && item.properties._free_gift);
  };

  // Read once; the bar and the gift logic share it.
  var giftConfig = (function () {
    var el = document.getElementById('FreeGiftConfig');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  })();

  // Amount of the gift discount (Buy X get Y) that landed on a line, matched by discount title.
  var giftDiscountOn = function (item) {
    var title = giftConfig && giftConfig.discountTitle ? String(giftConfig.discountTitle).toLowerCase() : '';
    if (!title || !item || !Array.isArray(item.line_level_discount_allocations)) return 0;
    return item.line_level_discount_allocations.reduce(function (sum, allocation) {
      var app = allocation.discount_application || {};
      return (app.title || '').toLowerCase() === title ? sum + (allocation.amount || 0) : sum;
    }, 0);
  };

  // Cart value that counts towards the goals: cart total (after discounts applied in the cart)
  // without the gift line. If Shopify put the gift discount on a paid line instead of the gift
  // line (same product in both), that amount is added back so the customer isn't penalised.
  var totalWithoutGift = function (cart) {
    if (!cart || !Array.isArray(cart.items)) return null;
    var total = cart.total_price || 0;
    cart.items.forEach(function (item) {
      if (isGiftLine(item)) total -= item.final_line_price || 0;
      else total += giftDiscountOn(item);
    });
    return total;
  };

  /* ---------- 1. Bar with milestones ---------- */
  if (!customElements.get('cart-goals')) {
    class CartGoals extends HTMLElement {
      connectedCallback() {
        this.onCartUpdated = this.onCartUpdated || this.handleCartUpdated.bind(this);
        document.addEventListener('cart:updated', this.onCartUpdated);
      }

      disconnectedCallback() {
        document.removeEventListener('cart:updated', this.onCartUpdated);
      }

      get config() {
        if (this._config === undefined) {
          try {
            this._config = JSON.parse(this.querySelector('[data-cart-goals]').textContent);
          } catch (e) {
            this._config = null;
          }
        }
        return this._config;
      }

      format(cents, currency) {
        var moneyFormat = this.dataset.moneyFormat;
        if (moneyFormat && typeof formatMoneyShopify === 'function') {
          return formatMoneyShopify(cents, moneyFormat, currency || 'AUD');
        }
        return '$' + (cents / 100).toFixed(2);
      }

      handleCartUpdated(event) {
        var cart = event && event.detail ? event.detail.cart : null;
        var total = totalWithoutGift(cart);
        if (total === null || !this.config) return;
        this.update(total, cart.currency);
      }

      update(total, currency) {
        var c = this.config;
        var progress = c.max > 0 ? Math.min((total / c.max) * 100, 100) : 0;
        this.style.setProperty('--goal-progress', progress.toFixed(2));

        var shipReached = c.ship > 0 && total >= c.ship;
        var giftReached = total >= c.gift;
        this.querySelectorAll('[data-goal="ship"]').forEach(function (el) { el.classList.toggle('is-reached', shipReached); });
        this.querySelectorAll('[data-goal="gift"]').forEach(function (el) { el.classList.toggle('is-reached', giftReached); });

        var message;
        if (c.ship > 0 && !shipReached) {
          message = c.tplShip.replace('[amount]', this.format(c.ship - total, currency));
        } else if (!giftReached) {
          message = c.tplGift.replace('[amount]', this.format(c.gift - total, currency));
          if (c.ship > 0 && c.tplShipDone) message = '<b>' + c.tplShipDone + '</b> ' + message;
        } else {
          message = c.tplAll;
        }
        var messageEl = this.querySelector('[data-cart-goals-message]');
        if (messageEl && messageEl.innerHTML !== message) messageEl.innerHTML = message;
      }
    }
    customElements.define('cart-goals', CartGoals);
  }

  /* ---------- 2. Auto add / remove the gift ---------- */
  var config = giftConfig;
  if (!config) return;
  var variantIds = Array.isArray(config.variantIds) ? config.variantIds.filter(Boolean) : [];
  var productIds = Array.isArray(config.productIds) ? config.productIds : [];
  var switchedOff = !!config.off;
  if (!switchedOff && (!config.autoAdd || !variantIds.length || !config.threshold)) return;

  var DECLINED_KEY = 'ww_free_gift_declined';
  var UNPRICED_KEY = 'ww_free_gift_unpriced';
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var cartUrl = (window.routes && window.routes.cart_url) || root + 'cart';

  var flag = {
    get: function (key) {
      try { return window.sessionStorage.getItem(key) === '1'; } catch (e) { return false; }
    },
    set: function (key, value) {
      try {
        if (value) window.sessionStorage.setItem(key, '1');
        else window.sessionStorage.removeItem(key);
      } catch (e) {}
    }
  };

  var busy = false;
  var pending = false;
  var timer = null;
  var giftWasInCart = null;

  var getCart = function () {
    return fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  };

  var postJSON = function (url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok || data.status) throw data;
        return data;
      });
    });
  };

  var removeLines = function (lines) {
    var updates = {};
    lines.forEach(function (line) { updates[line.key] = 0; });
    return postJSON(cartUrl + '/update.js', { updates: updates });
  };

  // Is the gift actually free? Either its own line is $0, or the gift discount sits on another
  // line of the same range (Shopify picked the paid one). Without a discount title we fall back
  // to "the gift-range lines carry at least one full gift's worth of discount".
  var giftIsFree = function (cart, gift) {
    if ((gift.final_line_price || 0) === 0) return true;
    if (config.discountTitle) {
      return cart.items.some(function (item) { return giftDiscountOn(item) > 0; });
    }
    var rangeDiscount = cart.items.reduce(function (sum, item) {
      if (productIds.indexOf(item.product_id) === -1) return sum;
      return sum + Math.max(0, (item.original_line_price || 0) - (item.final_line_price || 0));
    }, 0);
    return rangeDiscount >= (gift.original_price || 0);
  };

  var shuffled = function (list) {
    var copy = list.slice();
    for (var i = copy.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = copy[i]; copy[i] = copy[j]; copy[j] = tmp;
    }
    return copy;
  };

  var addGift = function () {
    var properties = { _free_gift: config.label || 'Free gift' };
    if (config.note) properties[config.label || 'Free gift'] = config.note;
    var queue = shuffled(variantIds).slice(0, 4);
    var tryNext = function () {
      var id = queue.shift();
      if (!id) return Promise.reject(new Error('No gift variant could be added'));
      return postJSON(cartUrl + '/add.js', { items: [{ id: id, quantity: 1, properties: properties }] })
        .catch(function (error) {
          // This design just sold out: try another one.
          console.warn('Free gift: variant ' + id + ' could not be added', error);
          return tryNext();
        });
    };
    return tryNext();
  };

  var refreshCartUI = function (cart) {
    var jobs = [];

    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContentsFromHTML === 'function' && typeof drawer.getSectionsToRender === 'function') {
      var drawerSections = drawer.getSectionsToRender();
      jobs.push(
        Promise.all(drawerSections.map(function (section) {
          return fetch(root + '?section_id=' + section.id).then(function (r) { return r.text(); });
        })).then(function (results) {
          var html = {};
          drawerSections.forEach(function (section, i) { html[section.id] = results[i]; });
          drawer.renderContentsFromHTML(html);
          drawer.classList.toggle('is-empty', cart.item_count === 0);
        })
      );
    }

    var cartPage = document.getElementById('main-cart-items') ? document.querySelector('cart-items') : null;
    if (cartPage && typeof cartPage.getSectionsToRender === 'function') {
      var pageSections = cartPage.getSectionsToRender();
      var names = pageSections.map(function (s) { return s.section; }).filter(function (v, i, a) { return v && a.indexOf(v) === i; });
      jobs.push(
        fetch(window.location.pathname + '?sections=' + names.join(','))
          .then(function (r) { return r.json(); })
          .then(function (sections) {
            pageSections.forEach(function (section) {
              var el = document.getElementById(section.id);
              var html = sections[section.section];
              if (!el || !html) return;
              var target = el.querySelector(section.selector) || el;
              target.innerHTML = cartPage.getSectionInnerHTML(html, section.selector);
            });
          })
      );
    }

    return Promise.all(jobs).catch(function (e) { console.error(e); }).then(function () {
      if (typeof updateCartBubbles === 'function') updateCartBubbles(cart.item_count);
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: cart, totalPrice: cart.total_price, items: cart.items, timestamp: Date.now(), source: 'free-gift' },
        bubbles: true
      }));
    });
  };

  var evaluate = function (cart) {
    var giftLines = cart.items.filter(isGiftLine);

    if (switchedOff) {
      return giftLines.length ? removeLines(giftLines) : null;
    }

    var paidLines = cart.items.filter(function (item) { return !isGiftLine(item); });
    var total = totalWithoutGift(cart);
    var eligible = paidLines.length > 0 && total >= config.threshold;

    if (!eligible) {
      flag.set(DECLINED_KEY, false);
      giftWasInCart = false;
      return giftLines.length ? removeLines(giftLines) : null;
    }

    if (giftLines.length) {
      giftWasInCart = true;
      if (!giftIsFree(cart, giftLines[0])) {
        // Never charge for the gift.
        console.warn('Free gift removed: it is not $0 in the cart. Check the Buy X get Y discount (title, dates, combinations).');
        flag.set(UNPRICED_KEY, true);
        giftWasInCart = false;
        return removeLines(giftLines);
      }
      var fix = {};
      var needsFix = false;
      giftLines.forEach(function (line, i) {
        var qty = i === 0 ? 1 : 0;
        if (line.quantity !== qty) { fix[line.key] = qty; needsFix = true; }
      });
      return needsFix ? postJSON(cartUrl + '/update.js', { updates: fix }) : null;
    }

    // Eligible, no gift in the cart
    if (giftWasInCart === true) {
      // The customer removed it themselves: respect that.
      flag.set(DECLINED_KEY, true);
      giftWasInCart = false;
      return null;
    }
    if (flag.get(DECLINED_KEY) || flag.get(UNPRICED_KEY)) return null;

    giftWasInCart = true;
    return addGift().catch(function (error) {
      // Everything sold out / unavailable: don't keep retrying this session.
      console.warn('Free gift could not be added', error);
      flag.set(DECLINED_KEY, true);
      giftWasInCart = false;
      return null;
    });
  };

  var run = function () {
    if (busy) { pending = true; return; }
    busy = true;
    getCart()
      .then(function (cart) { return evaluate(cart); })
      .then(function (changed) {
        if (!changed) return null;
        return getCart().then(function (cart) {
          // Straight after adding: make sure Shopify really made it free.
          var gift = cart.items.filter(isGiftLine)[0];
          if (!switchedOff && gift && !giftIsFree(cart, gift)) {
            console.warn('Free gift removed: it is not $0 in the cart. Check the Buy X get Y discount (title, dates, combinations).');
            flag.set(UNPRICED_KEY, true);
            giftWasInCart = false;
            return removeLines(cart.items.filter(isGiftLine)).then(getCart);
          }
          return cart;
        }).then(refreshCartUI);
      })
      .catch(function (e) { console.error(e); })
      .then(function () {
        busy = false;
        if (pending) { pending = false; schedule(); }
      });
  };

  var schedule = function () {
    clearTimeout(timer);
    timer = setTimeout(run, 350);
  };

  document.addEventListener('cart:updated', function (event) {
    if (event.detail && event.detail.source === 'free-gift') return;
    schedule();
  });

  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule);
})();
